import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Tab,
  Tabs,
  Stack,
  Button,
  Dialog,
  Typography,
  IconButton,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Label from 'src/components/label';
import { fDateTime } from 'src/utils/format-time';

const isHtmlEmpty = (html) =>
  !html || String(html).replace(/<(.|\n)*?>/g, '').replace(/&nbsp;/g, '').trim() === '';

export default function TcViewDialog({ open, onClose, row }) {
  const [tab, setTab] = useState('en');

  if (!row) return null;

  const content = tab === 'en' ? row.en : row.ka;
  const empty = isHtmlEmpty(content);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      onTransitionExited={() => setTab('en')}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        sx={{ p: 3, pb: 1 }}
      >
        <Stack spacing={0.75}>
          <Typography variant="h6">{row.title}</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Label variant="soft" color={row.active ? 'success' : 'default'}>
              {row.active ? 'Active' : 'Inactive'}
            </Label>
            <Typography variant="caption" color="text.secondary">
              Created {fDateTime(row.created_at)}
            </Typography>
          </Stack>
        </Stack>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>

      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        sx={{ px: 3, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
      >
        <Tab value="en" label="English" />
        <Tab value="ka" label="Georgian" />
      </Tabs>

      <DialogContent sx={{ pt: 3 }}>
        {empty ? (
          <Typography variant="body2" color="text.secondary">
            No content
          </Typography>
        ) : (
          <Box
            className="ql-snow"
            sx={{
              maxHeight: 480,
              overflowY: 'auto',
              fontFamily: 'inherit',
              fontSize: 14,
              lineHeight: 1.7,
              color: 'text.primary',
              '& p': { m: 0, mb: 1 },
              '& ul, & ol': { pl: 3, mb: 1 },
              '& h1, & h2, & h3': { mt: 2, mb: 1 },
              '& blockquote': {
                borderLeft: (theme) => `4px solid ${theme.palette.divider}`,
                pl: 2,
                color: 'text.secondary',
                m: 0,
                mb: 1,
              },
              '& a': { color: 'primary.main' },
            }}
          >
            <Box
              className="ql-editor"
              sx={{ p: 0 }}
              dangerouslySetInnerHTML={{ __html: content }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

TcViewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  row: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    en: PropTypes.string,
    ka: PropTypes.string,
    active: PropTypes.bool,
    created_at: PropTypes.string,
  }),
};

TcViewDialog.defaultProps = {
  row: null,
};
